import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HardDrive,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Database,
  Layers,
  FileCode,
  Activity,
  Check,
  Copy,
  Info,
  Clock,
  Search,
  ShieldAlert,
  BarChart3,
  Sparkles,
  Terminal,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';

export interface MSSQLStorageViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface MSSQLStorageKPIs {
  allocated_mb?: number | null;
  data_used_mb?: number | null;
  data_used_pct?: number | null;
  log_used_pct?: number | null;
  log_status?: string | null;
  open_alerts?: {
    total?: number | null;
    warnings?: number | null;
    critical?: number | null;
    failed_jobs?: number | null;
  };
}

export interface MSSQLStorageAllocation {
  database: string;
  data_mb: number;
  log_mb: number;
}

export interface MSSQLStorageCapacity {
  data_used_mb?: number | null;
  data_free_in_files_mb?: number | null;
  log_allocated_mb?: number | null;
  data_used_pct?: number | null;
  largest_database?: string | null;
  largest_database_growth_7d_pct?: number | null;
}

export interface MSSQLStorageFile {
  file_id: number;
  growth_pct?: number | null;
  growth_mb?: number | null;
  max_size?: string | null;
  name: string;
  physical_name: string;
  size_mb: number;
  type: string;
  used_mb?: number | null;
  volume_free_mb?: number | null;
  volume_total_mb?: number | null;
}

export interface MSSQLStorageDatabase {
  database: string;
  is_system?: boolean;
  state?: string;
  recovery_model?: string;
  data_allocated_mb?: number | null;
  data_used_mb?: number | null;
  data_used_pct?: number | null;
  data_max_size_mb?: number | null;
  log_allocated_mb?: number | null;
  log_used_mb?: number | null;
  log_used_pct?: number | null;
  log_reuse_wait?: string | null;
  volume_free_mb?: number | null;
  volume_free_pct?: number | null;
  status?: string;
  status_reasons?: Array<{ severity: string; reason: string }>;
  files?: MSSQLStorageFile[];
}

export interface MSSQLStorageVolume {
  mount?: string | null;
  total_mb?: number | null;
  free_mb?: number | null;
  databases?: string[];
  free_pct?: number | null;
  status?: string;
}

export interface MSSQLStorageTempDB {
  status?: string;
  data_used_pct?: number | null;
  log_used_pct?: number | null;
  data_files?: number | null;
  version_store_mb?: number | null;
  internal_objects_mb?: number | null;
  user_objects_mb?: number | null;
  free_mb?: number | null;
}

export interface MSSQLStorageFileIO {
  available?: boolean;
  window_minutes?: number | null;
  files_measured?: number | null;
  avg_read_latency_ms?: number | null;
  avg_write_latency_ms?: number | null;
  read_throughput_mb_s?: number | null;
  write_throughput_mb_s?: number | null;
  read_throughput_kb_s?: number | null;
  write_throughput_kb_s?: number | null;
  reason?: string | null;
}

export interface MSSQLStorageStaleStat {
  modifications?: number | null;
  reason?: string | null;
  rows?: number | null;
  schema?: string | null;
  stat?: string | null;
  table?: string | null;
  threshold?: number | null;
  database?: string | null;
}

export interface MSSQLStorageCheckDBItem {
  database: string;
  last_good_checkdb?: string | null;
  detail?: string | null;
}

export interface MSSQLStorageMaintenance {
  fragmented_indexes?: number | null;
  indexes_checked?: number | null;
  stale_statistics?: number | null;
  statistics_checked?: number | null;
  failed_jobs?: number | null;
  jobs_capability?: {
    detail?: string | null;
    mechanism?: string | null;
    status?: string | null;
  };
  file_growth_events_24h?: number | null;
  checkdb_overdue?: number | null;
  option_warnings?: number | null;
  high_vlf_databases?: number | null;
  suspect_pages?: number | null;
  coverage?: {
    databases?: number | null;
    databases_checked?: number | null;
    oldest_check?: string | null;
    interval_hours?: number | null;
    unavailable?: Record<string, any>;
  };
  fragmented_index_list?: any[];
  stale_statistics_list?: MSSQLStorageStaleStat[];
  checkdb?: MSSQLStorageCheckDBItem[];
  options?: any[];
  vlf?: any[];
  status?: string;
}

export interface MSSQLStorageEvent {
  time?: string | null;
  database?: string | null;
  event?: string | null;
  details?: string | null;
  severity?: string | null;
}

export interface MSSQLFindingItem {
  severity: 'critical' | 'warning' | 'info' | string;
  category: string;
  database?: string | null;
  object?: string | null;
  finding: string;
  impact: string;
  remediation: string;
  command?: string | null;
}

export interface MSSQLFindingsData {
  note?: string;
  count?: number;
  by_severity?: Record<string, number>;
  items?: MSSQLFindingItem[];
}

export interface MSSQLStorageOverviewResponse {
  status: boolean;
  applicable?: boolean;
  kpis?: MSSQLStorageKPIs;
  allocation?: MSSQLStorageAllocation[];
  capacity?: MSSQLStorageCapacity;
  databases?: MSSQLStorageDatabase[];
  volumes?: MSSQLStorageVolume[];
  tempdb?: MSSQLStorageTempDB;
  file_io?: MSSQLStorageFileIO;
  maintenance?: MSSQLStorageMaintenance;
  events?: MSSQLStorageEvent[];
  findings?: MSSQLFindingsData;
  thresholds?: {
    warn_pct?: number;
    critical_pct?: number;
    volume_free_warn_pct?: number;
    volume_free_critical_pct?: number;
    fragmentation_pct?: number;
    fragmentation_min_pages?: number;
    checkdb_stale_days?: number;
    vlf_warn?: number;
    stale_statistics?: string;
  };
  [key: string]: any;
}

// Format number helper
const formatNumber = (val: number | null | undefined, decimals = 2): string => {
  if (val === null || val === undefined || isNaN(val)) return '—';
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

// Format MB with unit scaling to GB if >= 1024
const formatMB = (mb: number | null | undefined): string => {
  if (mb === null || mb === undefined || isNaN(mb)) return '—';
  const num = Number(mb);
  if (Math.abs(num) >= 1024) {
    return `${(num / 1024).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GB`;
  }
  return `${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MB`;
};

// Format Percent
const formatPct = (pct: number | null | undefined): string => {
  if (pct === null || pct === undefined || isNaN(pct)) return '—';
  return `${Number(pct).toFixed(2)}%`;
};

// Format Date
const formatDate = (isoStr: string | null | undefined): string => {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    return d.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return isoStr;
  }
};

// Color helper for utilization percentage
const getPctColorClass = (pct: number | null | undefined) => {
  if (pct === null || pct === undefined) return 'text-gray-500 dark:text-gray-200';
  if (pct >= 90) return 'text-rose-600 dark:text-rose-400';
  if (pct >= 80) return 'text-amber-500 dark:text-amber-400';
  return 'text-blue-600 dark:text-blue-400';
};

const getPctBgClass = (pct: number | null | undefined) => {
  if (pct === null || pct === undefined) return 'bg-gray-400';
  if (pct >= 90) return 'bg-rose-500';
  if (pct >= 80) return 'bg-amber-500';
  return 'bg-blue-500';
};

// Custom tooltip for Utilization Recharts graph
const UtilizationTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 shadow-xl text-xs text-gray-900 dark:text-gray-100 min-w-[140px] z-50 space-y-1.5">
        <div className="font-bold border-b border-gray-100 dark:border-gray-800 pb-1 text-gray-800 dark:text-gray-200">
          {label}
        </div>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center justify-between gap-3 text-[11px]">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-200">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              {entry.name}:
            </span>
            <span className="font-mono font-bold text-gray-900 dark:text-white">
              {entry.value}%
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const MSSQLStorageView: React.FC<MSSQLStorageViewProps> = ({
  activeCluster,
  timeRange = '60',
  clientId,
  refreshKey,
  dbType = 'MSSQL',
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<MSSQLStorageOverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters & Interactivity
  const [selectedDb, setSelectedDb] = useState<string>('all');
  const [dbSearch, setDbSearch] = useState<string>('');
  const [fileSearch, setFileSearch] = useState<string>('');
  const [statSearch, setStatSearch] = useState<string>('');
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'inventory' | 'files' | 'findings' | 'events' | 'stale_stats' | 'checkdb'>('inventory');

  // Fetch Storage & Maintenance Overview
  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    setError(null);
    try {
      const minutes = parseInt(timeRange || '60', 10);
      const params: any = {
        cluster_id: activeCluster,
        minutes: isNaN(minutes) ? 60 : minutes,
      };
      if (clientId) {
        params.client_id = clientId;
      }

      const res = await baseapi.get('/db_monitor_dashboard/mssql/storage-maintenance/overview', { params });
      if (res?.data) {
        setData(res.data?.data || res.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch MSSQL storage overview:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load MSSQL storage overview');
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Copy helper
  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedPath(text);
    setTimeout(() => setCopiedPath(null), 2000);
  };

  // Safe references
  const kpis = data?.kpis;
  const capacity = data?.capacity;
  const rawDatabases = useMemo(() => data?.databases || [], [data?.databases]);
  const rawAllocation = useMemo(() => data?.allocation || [], [data?.allocation]);
  const volumes = useMemo(() => data?.volumes || [], [data?.volumes]);
  const tempdb = data?.tempdb;
  const fileIo = data?.file_io;
  const maintenance = data?.maintenance;
  const events = useMemo(() => data?.events || [], [data?.events]);
  const findings = data?.findings;

  // Filtered findings
  const filteredFindings = useMemo(() => {
    const list = findings?.items || [];
    return list.filter((item) => {
      const matchesDb =
        selectedDb === 'all' ||
        !item.database ||
        item.database.toLowerCase() === selectedDb.toLowerCase();
      const matchesSearch =
        !dbSearch.trim() ||
        (item.finding && item.finding.toLowerCase().includes(dbSearch.toLowerCase())) ||
        (item.category && item.category.toLowerCase().includes(dbSearch.toLowerCase())) ||
        (item.object && item.object.toLowerCase().includes(dbSearch.toLowerCase())) ||
        (item.database && item.database.toLowerCase().includes(dbSearch.toLowerCase()));
      return matchesDb && matchesSearch;
    });
  }, [findings, selectedDb, dbSearch]);

  // List of database names for dropdown filter
  const databaseOptions = useMemo(() => {
    const names = new Set<string>();
    rawDatabases.forEach((d) => names.add(d.database));
    rawAllocation.forEach((a) => names.add(a.database));
    return Array.from(names).sort();
  }, [rawDatabases, rawAllocation]);

  // Dropdown options for CustomDropdown
  const dbDropdownOptions = useMemo(() => [
    { label: `All databases (${databaseOptions.length})`, value: 'all' },
    ...databaseOptions.map((name) => ({ label: name, value: name })),
  ], [databaseOptions]);

  // Filtered databases
  const filteredDatabases = useMemo(() => {
    return rawDatabases.filter((d) => {
      const matchesDb = selectedDb === 'all' || d.database.toLowerCase() === selectedDb.toLowerCase();
      const matchesSearch = !dbSearch.trim() || d.database.toLowerCase().includes(dbSearch.toLowerCase()) || (d.recovery_model || '').toLowerCase().includes(dbSearch.toLowerCase());
      return matchesDb && matchesSearch;
    });
  }, [rawDatabases, selectedDb, dbSearch]);

  // Chart data for Utilization by Database via Recharts
  const utilizationChartData = useMemo(() => {
    return filteredDatabases.map((db) => ({
      name: db.database,
      data_pct: Number((db.data_used_pct || 0).toFixed(1)),
      log_pct: Number((db.log_used_pct || 0).toFixed(1)),
      data_mb: db.data_used_mb,
      log_mb: db.log_used_mb,
    }));
  }, [filteredDatabases]);

  // Filtered allocation for visual bar
  const filteredAllocation = useMemo(() => {
    return rawAllocation.filter((a) => selectedDb === 'all' || a.database.toLowerCase() === selectedDb.toLowerCase());
  }, [rawAllocation, selectedDb]);

  // Max total allocation for proportional horizontal bars
  const maxAllocMb = useMemo(() => {
    if (!filteredAllocation.length) return 1;
    return Math.max(...filteredAllocation.map((d) => (d.data_mb || 0) + (d.log_mb || 0)), 1);
  }, [filteredAllocation]);

  // Filtered files
  const filteredFiles = useMemo(() => {
    const list: Array<{ dbName: string; file: MSSQLStorageFile }> = [];
    rawDatabases.forEach((d) => {
      if (selectedDb === 'all' || d.database.toLowerCase() === selectedDb.toLowerCase()) {
        (d.files || []).forEach((f) => {
          if (
            !fileSearch.trim() ||
            f.name.toLowerCase().includes(fileSearch.toLowerCase()) ||
            f.physical_name.toLowerCase().includes(fileSearch.toLowerCase()) ||
            d.database.toLowerCase().includes(fileSearch.toLowerCase()) ||
            f.type.toLowerCase().includes(fileSearch.toLowerCase())
          ) {
            list.push({ dbName: d.database, file: f });
          }
        });
      }
    });
    return list;
  }, [rawDatabases, selectedDb, fileSearch]);

  // Filtered stale statistics
  const filteredStaleStats = useMemo(() => {
    const list = maintenance?.stale_statistics_list || [];
    return list.filter((s) => {
      const matchesDb = selectedDb === 'all' || (s.database || '').toLowerCase() === selectedDb.toLowerCase();
      const matchesSearch =
        !statSearch.trim() ||
        (s.table || '').toLowerCase().includes(statSearch.toLowerCase()) ||
        (s.stat || '').toLowerCase().includes(statSearch.toLowerCase()) ||
        (s.schema || '').toLowerCase().includes(statSearch.toLowerCase()) ||
        (s.reason || '').toLowerCase().includes(statSearch.toLowerCase());
      return matchesDb && matchesSearch;
    });
  }, [maintenance?.stale_statistics_list, selectedDb, statSearch]);

  // Filtered events
  const filteredEvents = useMemo(() => {
    return events.filter((e) => selectedDb === 'all' || (e.database || '').toLowerCase() === selectedDb.toLowerCase());
  }, [events, selectedDb]);

  // Filtered checkdb
  const filteredCheckDB = useMemo(() => {
    const list = maintenance?.checkdb || [];
    return list.filter((c) => selectedDb === 'all' || (c.database || '').toLowerCase() === selectedDb.toLowerCase());
  }, [maintenance?.checkdb, selectedDb]);

  // Calculate volume stats
  const primaryVolume = volumes.length > 0 ? volumes[0] : null;
  const volumeFreeMb = primaryVolume?.free_mb ?? (filteredDatabases.length > 0 ? filteredDatabases[0].volume_free_mb : null);
  const volumeFreePct = primaryVolume?.free_pct ?? (filteredDatabases.length > 0 ? filteredDatabases[0].volume_free_pct : null);

  // Overall status
  const overallStatus = useMemo(() => {
    if ((kpis?.open_alerts?.critical || 0) > 0) return { label: 'CRITICAL', color: 'rose' };
    if ((kpis?.open_alerts?.warnings || 0) > 0 || (volumeFreePct != null && volumeFreePct <= 20) || maintenance?.status === 'attention') {
      return { label: 'ATTENTION REQUIRED', color: 'amber' };
    }
    return { label: 'HEALTHY', color: 'emerald' };
  }, [kpis, volumeFreePct, maintenance]);

  // Data capacity used pct
  const capacityPct = capacity?.data_used_pct ?? kpis?.data_used_pct ?? 0;
  const capacityUsedMb = capacity?.data_used_mb ?? kpis?.data_used_mb ?? 0;
  const capacityFreeMb = capacity?.data_free_in_files_mb ?? 0;

  // Render Loading Skeleton
  if (loading && !data) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-800 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-800" />
            <div className="space-y-2">
              <div className="h-4 w-48 bg-gray-200 dark:bg-gray-800 rounded" />
              <div className="h-3 w-64 bg-gray-200 dark:bg-gray-800 rounded" />
            </div>
          </div>
          <div className="h-9 w-32 bg-gray-200 dark:bg-gray-800 rounded" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-28 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Render Error State
  if (error && !data) {
    return (
      <div className="p-8 rounded-xl bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 text-center space-y-4">
        <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
        <div>
          <h3 className="text-base font-semibold text-rose-900 dark:text-rose-200">
            Failed to Load SQL Server Storage & Maintenance Overview
          </h3>
          <p className="text-sm text-rose-600 dark:text-rose-400 mt-1 max-w-lg mx-auto">{error}</p>
        </div>
        <button
          onClick={() => fetchData()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold shadow-sm transition-colors cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ─────────────────────────────────────────────────────────────
          TOP BANNER HEADER & CONTROLS
          ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 pb-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Storage & Maintenance
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30">
                {(dbType || 'MSSQL').toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-200 mt-0.5">
              Storage capacity, allocation, physical file I/O, TempDB telemetry & database maintenance
            </p>
          </div>
        </div>

        {/* Controls: Status pill, Database Selector, Reset & Refresh */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* Status Badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm ${
              overallStatus.color === 'rose'
                ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-500/30'
                : overallStatus.color === 'amber'
                ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                overallStatus.color === 'rose'
                  ? 'bg-rose-500 animate-pulse'
                  : overallStatus.color === 'amber'
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'
              }`}
            />
            {overallStatus.label}
          </span>

          {/* Database Filter CustomDropdown */}
          <CustomDropdown
            data={dbDropdownOptions}
            selectedValue={selectedDb}
            onSelect={(val) => setSelectedDb(val)}
            width="w-48 sm:w-56"
            placeholder="Select database"
            buttonHeight={8}
          />

          {/* Reset Button */}
          {selectedDb !== 'all' && (
            <button
              onClick={() => {
                setSelectedDb('all');
                setDbSearch('');
                setFileSearch('');
                setStatSearch('');
              }}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-700 transition-colors cursor-pointer"
            >
              Reset
            </button>
          )}


        
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 0: 5 TOP KPI CARDS
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
        {/* KPI 1: Data Allocated */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">Data allocated</div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatMB(kpis?.allocated_mb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200">Across reported database files</div>
        </div>

        {/* KPI 2: Data Used */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">Data used</div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatMB(kpis?.data_used_mb)}
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 mb-1.5">
              {formatPct(kpis?.data_used_pct)} of reported capacity
            </div>
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${getPctBgClass(kpis?.data_used_pct)} transition-all duration-500`}
                style={{ width: `${Math.min(kpis?.data_used_pct || 0, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* KPI 3: Log Used */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">Log used</div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatPct(kpis?.log_used_pct)}
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 mb-1.5 flex items-center gap-1">
              <span>Log status:</span>
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                {kpis?.log_status || 'healthy'}
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${Math.min(kpis?.log_used_pct || 0, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* KPI 4: Open Alerts */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">Open alerts</div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {kpis?.open_alerts?.total ?? 0}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200">
            <span className="text-amber-600 dark:text-amber-400 font-semibold">
              {kpis?.open_alerts?.warnings ?? 0} warnings
            </span>{' '}
            ·{' '}
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
              {kpis?.open_alerts?.critical ?? 0} critical
            </span>
          </div>
        </div>

        {/* KPI 5: Volume Free Space */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">Volume free</div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatMB(volumeFreeMb)}
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 mb-1.5">
              <span className={volumeFreePct != null && volumeFreePct <= 20 ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>
                {formatPct(volumeFreePct)} free
              </span>{' '}
              · threshold 20%
            </div>
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${volumeFreePct != null && volumeFreePct <= 20 ? 'bg-amber-500' : 'bg-emerald-500'} transition-all duration-500`}
                style={{ width: `${Math.min(volumeFreePct || 0, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 1: ALLOCATION, UTILIZATION, DATA CAPACITY & VOLUME GAUGES
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
        {/* Card 1: Data & log allocation (MB) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Data & Log Allocation
              </h3>
              <span className="text-[11px] font-mono text-gray-600 dark:text-gray-200">MB</span>
            </div>

            {/* Proportional horizontal bars per database (compact) */}
            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
              {filteredAllocation.length === 0 ? (
                <div className="py-6 text-center text-xs text-gray-200">No allocation data</div>
              ) : (
                filteredAllocation.map((item) => {
                  const total = (item.data_mb || 0) + (item.log_mb || 0);
                  const barWidthPct = Math.min((total / maxAllocMb) * 100, 100);
                  const dataPctOfBar = total > 0 ? (item.data_mb / total) * 100 : 0;
                  return (
                    <div key={item.database} className="space-y-0.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 text-xs">
                          {item.database}
                        </span>
                        <span className="font-mono text-gray-700 dark:text-gray-300 font-bold text-[11px]">
                          {formatNumber(total, 2)} MB
                        </span>
                      </div>
                      {/* Compact Stacked Proportional Bar */}
                      <div className="w-full bg-gray-100 dark:bg-gray-700/60 h-2 rounded-full overflow-hidden flex">
                        <div
                          className="h-full flex rounded-full overflow-hidden"
                          style={{ width: `${Math.max(barWidthPct, 4)}%` }}
                        >
                          <div
                            className="bg-blue-500 h-full"
                            style={{ width: `${dataPctOfBar}%` }}
                            title={`Data: ${formatNumber(item.data_mb, 2)} MB`}
                          />
                          <div
                            className="bg-purple-500 h-full flex-1"
                            title={`Log: ${formatNumber(item.log_mb, 2)} MB`}
                          />
                        </div>
                      </div>
                      <div className="text-[10px] text-gray-600 dark:text-gray-200 flex justify-between leading-none pt-0.5">
                        <span>Data {formatNumber(item.data_mb, 2)} MB</span>
                        <span>Log {formatNumber(item.log_mb, 2)} MB</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-200 pt-3 border-t border-gray-100 dark:border-gray-700 mt-2">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded bg-blue-500" />
              <span>Data</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded bg-purple-500" />
              <span>Log</span>
            </span>
          </div>
        </div>

        {/* Card 2: Utilization by database (% used) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              Utilization by Database
            </h3>
            <span className="text-[11px] font-mono text-gray-200">% used</span>
          </div>

          {/* Recharts Bar Graph - expanded to fill card height */}
          <div className="flex-1 w-full min-h-[220px] pt-1 pb-1">
            {utilizationChartData.length === 0 ? (
              <div className="w-full h-full flex items-center justify-center text-xs text-gray-200">
                No database metrics
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={utilizationChartData}
                  margin={{ top: 12, right: 6, left: -22, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-gray-100 dark:text-gray-700/60" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: 'currentColor', fontSize: 10 }}
                    className="text-gray-500 dark:text-gray-200 font-mono"
                    tickLine={false}
                    axisLine={{ stroke: 'currentColor', className: 'text-gray-200 dark:text-gray-700' }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    tick={{ fill: 'currentColor', fontSize: 9 }}
                    className="text-gray-500 dark:text-gray-200 font-mono"
                    tickFormatter={(val) => `${val}%`}
                    tickLine={false}
                    axisLine={{ stroke: 'currentColor', className: 'text-gray-200 dark:text-gray-700' }}
                  />
                  <RechartsTooltip content={<UtilizationTooltip />}  cursor={false}/>
                  <ReferenceLine
                    y={80}
                    stroke="#f59e0b"
                    strokeDasharray="3 3"
                    label={{ value: '80% warn', position: 'insideTopRight', fill: '#f59e0b', fontSize: 9 }}
                  />
                  <ReferenceLine
                    y={90}
                    stroke="#ef4444"
                    strokeDasharray="3 3"
                    label={{ value: '90% crit', position: 'insideTopRight', fill: '#ef4444', fontSize: 9 }}
                  />
                  <Bar
                    dataKey="data_pct"
                    name="Data %"
                    fill="#3b82f6"
                    radius={[3, 3, 0, 0]}
                    maxBarSize={28}
                  />
                  <Bar
                    dataKey="log_pct"
                    name="Log %"
                    fill="#10b981"
                    radius={[3, 3, 0, 0]}
                    maxBarSize={28}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-200 pt-3 border-t border-gray-100 dark:border-gray-700 mt-2">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded bg-blue-500" />
              <span>Data</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded bg-emerald-500" />
              <span>Log</span>
            </span>
            <span className="text-amber-500 text-[10px]">- - 80% warn</span>
            <span className="text-rose-500 text-[10px]">- - 90% crit</span>
          </div>
        </div>

        {/* Card 3: Data Capacity Circular Gauge */}
        <div className="lg:col-span-2 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col items-center justify-between">
          <div className="w-full flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              Data Capacity
            </h3>
            <span className="text-[11px] font-mono text-gray-200">MB</span>
          </div>

          {/* SVG Donut */}
          <div className="relative flex items-center justify-center my-auto">
            {(() => {
              const r = 46;
              const circ = 2 * Math.PI * r;
              const usedOffset = (Math.min(capacityPct, 100) / 100) * circ;
              return (
                <svg viewBox="0 0 120 120" className="w-32 h-32">
                  <circle
                    cx="60"
                    cy="60"
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="14"
                    className="text-gray-100 dark:text-gray-700/80"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="14"
                    strokeDasharray={`${usedOffset} ${circ}`}
                    transform="rotate(-90 60 60)"
                    strokeLinecap="round"
                    className={
                      capacityPct >= 90
                        ? 'text-rose-500'
                        : capacityPct >= 80
                        ? 'text-amber-500'
                        : 'text-blue-500'
                    }
                  />
                  <text
                    x="60"
                    y="57"
                    textAnchor="middle"
                    className="fill-gray-900 dark:fill-white font-bold font-mono text-[18px]"
                  >
                    {capacityPct.toFixed(1)}%
                  </text>
                  <text
                    x="60"
                    y="72"
                    textAnchor="middle"
                    className="fill-gray-500 dark:fill-gray-400 text-[10px] font-medium"
                  >
                    used
                  </text>
                </svg>
              );
            })()}
          </div>

          {/* Legend */}
          <div className="w-full flex flex-col gap-1 text-[11px] text-gray-500 dark:text-gray-200 pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="flex justify-between">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded bg-blue-500" />
                <span>Used</span>
              </span>
              <span className="font-mono text-gray-700 dark:text-gray-300 font-semibold">
                {formatNumber(capacityUsedMb, 1)} MB
              </span>
            </div>
            <div className="flex justify-between">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded bg-gray-300 dark:bg-gray-600" />
                <span>Free in files</span>
              </span>
              <span className="font-mono text-gray-700 dark:text-gray-300 font-semibold">
                {formatNumber(capacityFreeMb, 1)} MB
              </span>
            </div>
          </div>
        </div>

        {/* Card 4: Volume Free Space Semi-circle Gauge */}
        <div className="lg:col-span-2 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col items-center justify-between">
          <div className="w-full flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              Volume Free Space
            </h3>
            <span
              className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                volumeFreePct != null && volumeFreePct <= 20
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
              }`}
            >
              {volumeFreePct != null && volumeFreePct <= 20 ? 'WARN' : 'HEALTHY'}
            </span>
          </div>

          {/* Semi-circular gauge SVG */}
          <div className="relative flex items-center justify-center my-auto">
            {(() => {
              const freeVal = Math.min(Math.max(volumeFreePct || 0, 0), 100);
              return (
                <svg viewBox="0 0 120 72" className="w-36 h-24">
                  {/* Background Arc */}
                  <path
                    d="M10 62A50 50 0 0 1 110 62"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="13"
                    strokeLinecap="round"
                    pathLength="100"
                    className="text-gray-200 dark:text-gray-700"
                  />
                  {/* Progress Arc */}
                  <path
                    d="M10 62A50 50 0 0 1 110 62"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="13"
                    strokeLinecap="round"
                    pathLength="100"
                    strokeDasharray={`${freeVal} 100`}
                    className={
                      freeVal <= 10
                        ? 'text-rose-500'
                        : freeVal <= 20
                        ? 'text-amber-500'
                        : 'text-emerald-500'
                    }
                  />
                  {/* Marker at 20% threshold */}
                  <path
                    d="M10 62A50 50 0 0 1 110 62"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="15"
                    pathLength="100"
                    strokeDasharray="0.8 100"
                    strokeDashoffset="-20"
                    className="text-gray-900 dark:text-gray-100"
                  />
                  <text
                    x="60"
                    y="56"
                    textAnchor="middle"
                    className="fill-gray-900 dark:fill-white font-bold font-mono text-[16px]"
                  >
                    {freeVal.toFixed(1)}%
                  </text>
                </svg>
              );
            })()}
          </div>

          {/* Footer note */}
          <div className="w-full text-center text-[10px] text-gray-500 dark:text-gray-200 pt-2 border-t border-gray-100 dark:border-gray-700">
            <span className="font-semibold text-gray-800 dark:text-gray-200 font-mono">
              {formatMB(volumeFreeMb)}
            </span>{' '}
            free · marker = 20%
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 2: 4 DIAGNOSTIC CARDS (TempDB, File I/O, Maintenance, Indexes)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {/* Card 1: TempDB */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-blue-500" />
              TempDB
            </h3>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
              {tempdb?.status || 'HEALTHY'}
            </span>
          </div>

          <div className="space-y-2 text-xs">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500 dark:text-gray-200">Data used</span>
                <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                  {formatPct(tempdb?.data_used_pct)}
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500"
                  style={{ width: `${Math.min(tempdb?.data_used_pct || 0, 100)}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500 dark:text-gray-200">Log used</span>
                <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                  {formatPct(tempdb?.log_used_pct)}
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${Math.min(tempdb?.log_used_pct || 0, 100)}%` }}
                />
              </div>
            </div>

            <div className="pt-1 border-t border-gray-100 dark:border-gray-700 space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-200">Internal / User obj:</span>
                <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                  {formatNumber(tempdb?.internal_objects_mb, 2)} / {formatNumber(tempdb?.user_objects_mb, 2)} MB
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-200">Free · Files:</span>
                <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                  {formatNumber(tempdb?.free_mb, 2)} MB · {tempdb?.data_files ?? 1} file(s)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: File I/O */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-purple-500" />
              File I/O
            </h3>
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                fileIo?.available
                  ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                  : 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
              }`}
            >
              {fileIo?.available ? 'AVAILABLE' : 'UNAVAILABLE'}
            </span>
          </div>

          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-200">Read / Write latency</span>
              <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                {fileIo?.avg_read_latency_ms != null ? `${fileIo.avg_read_latency_ms} ms` : '—'} /{' '}
                {fileIo?.avg_write_latency_ms != null ? `${fileIo.avg_write_latency_ms} ms` : '—'}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-200">Read / Write throughput</span>
              <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                {fileIo?.read_throughput_mb_s != null ? `${fileIo.read_throughput_mb_s} MB/s` : '—'} /{' '}
                {fileIo?.write_throughput_mb_s != null ? `${fileIo.write_throughput_mb_s} MB/s` : '—'}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-gray-500 dark:text-gray-200">Window · Files measured</span>
              <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                {fileIo?.window_minutes ?? 60} min · {fileIo?.files_measured ?? 0}
              </span>
            </div>
            {fileIo?.reason && (
              <div className="p-2 rounded-lg bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30 text-[11px] text-amber-800 dark:text-amber-300 leading-snug">
                {fileIo.reason}
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Maintenance */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
              Maintenance
            </h3>
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                maintenance?.status === 'attention'
                  ? 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                  : 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
              }`}
            >
              {maintenance?.status === 'attention' ? 'ATTENTION' : 'HEALTHY'}
            </span>
          </div>

          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-200">Databases checked</span>
              <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                {maintenance?.coverage?.databases_checked ?? 0} / {maintenance?.coverage?.databases ?? rawDatabases.length}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-200">Oldest check</span>
              <span className="font-mono text-gray-700 dark:text-gray-300 text-[11px] truncate max-w-[150px]">
                {formatDate(maintenance?.coverage?.oldest_check)}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-200">Interval · Overdue CHECKDB</span>
              <span className="font-mono font-semibold">
                {maintenance?.coverage?.interval_hours ?? 6}h ·{' '}
                <span className={maintenance?.checkdb_overdue ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>
                  {maintenance?.checkdb_overdue ?? 0}
                </span>
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-gray-500 dark:text-gray-200">Failed jobs · SQL Agent</span>
              <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                {maintenance?.failed_jobs ?? '—'} ·{' '}
                <span
                  className={
                    maintenance?.jobs_capability?.status === 'supported_disabled'
                      ? 'text-amber-600 dark:text-amber-400 font-bold'
                      : 'text-emerald-600 dark:text-emerald-400'
                  }
                >
                  {maintenance?.jobs_capability?.status === 'supported_disabled' ? 'STOPPED' : 'ACTIVE'}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Card 4: Indexes & Config */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-indigo-500" />
              Indexes & Config
            </h3>
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                (maintenance?.stale_statistics || 0) > 0 || (maintenance?.fragmented_indexes || 0) > 0
                  ? 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                  : 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
              }`}
            >
              {(maintenance?.stale_statistics || 0) > 0
                ? `${maintenance?.stale_statistics} WARNING`
                : 'HEALTHY'}
            </span>
          </div>

          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-200">Fragmented / Checked idx</span>
              <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                {maintenance?.fragmented_indexes ?? 0} / {maintenance?.indexes_checked ?? 0}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-200">Stale / Checked stats</span>
              <span className="font-mono font-bold">
                <span className={(maintenance?.stale_statistics || 0) > 0 ? 'text-amber-600 dark:text-amber-400' : ''}>
                  {maintenance?.stale_statistics ?? 0}
                </span>{' '}
                / {maintenance?.statistics_checked ?? 0}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-200">Growth 24h · Suspect · VLF</span>
              <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                {maintenance?.file_growth_events_24h ?? 0} · {maintenance?.suspect_pages ?? 0} · {maintenance?.high_vlf_databases ?? 0}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-gray-500 dark:text-gray-200">Largest · 7-day growth</span>
              <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                {capacity?.largest_database || '—'} · {capacity?.largest_database_growth_7d_pct != null ? `${capacity.largest_database_growth_7d_pct}%` : 'n/a'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 3 & 4: INTERACTIVE TABLES & AUDIT DETAILS
          ───────────────────────────────────────────────────────────── */}
      <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        {/* Navigation Sub-Tabs */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 pt-3 pb-0 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('inventory')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'inventory'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Database className="w-3.5 h-3.5" />
              Database Inventory ({filteredDatabases.length})
            </button>
            <button
              onClick={() => setActiveTab('files')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'files'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <FileCode className="w-3.5 h-3.5" />
              Database Files ({filteredFiles.length})
            </button>
            <button
              onClick={() => setActiveTab('findings')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'findings'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              Automated Findings ({filteredFindings.length})
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'events'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Clock className="w-3.5 h-3.5" />
              Recent Events ({filteredEvents.length})
            </button>
            <button
              onClick={() => setActiveTab('stale_stats')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'stale_stats'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              Stale Statistics ({filteredStaleStats.length})
            </button>
            <button
              onClick={() => setActiveTab('checkdb')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'checkdb'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Integrity Check ({filteredCheckDB.length})
            </button>
          </div>
        </div>

        {/* ── SUB-TAB 1: DATABASE INVENTORY ── */}
        {activeTab === 'inventory' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter database name or recovery model..."
                  value={dbSearch}
                  onChange={(e) => setDbSearch(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="text-xs text-gray-500">
                Showing <span className="font-semibold">{filteredDatabases.length}</span> database(s)
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">State</th>
                    <th className="py-2.5 px-3">Recovery</th>
                    <th className="py-2.5 px-3">Data Alloc.</th>
                    <th className="py-2.5 px-3">Data Used</th>
                    <th className="py-2.5 px-3 min-w-[120px]">Data %</th>
                    <th className="py-2.5 px-3">Log Alloc.</th>
                    <th className="py-2.5 px-3">Log Used</th>
                    <th className="py-2.5 px-3 min-w-[110px]">Log %</th>
                    <th className="py-2.5 px-3 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredDatabases.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-gray-200">
                        No databases found matching filter
                      </td>
                    </tr>
                  ) : (
                    filteredDatabases.map((db) => {
                      const dp = db.data_used_pct || 0;
                      const lp = db.log_used_pct || 0;
                      return (
                        <tr
                          key={db.database}
                          className="hover:bg-gray-50/70 dark:hover:bg-gray-700/40 transition-colors"
                        >
                          <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                            <Database className="w-3 h-3 text-gray-200 shrink-0" />
                            <span>{db.database}</span>
                            {db.is_system && (
                              <span className="px-1 py-0.5 rounded text-[9px] font-mono uppercase bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-200">
                                sys
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-[11px]">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30">
                              {db.state || 'ONLINE'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 font-mono text-[11px] text-gray-600 dark:text-gray-300">
                            {db.recovery_model || '—'}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-gray-700 dark:text-gray-300">
                            {formatNumber(db.data_allocated_mb, 2)}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-gray-700 dark:text-gray-300">
                            {formatNumber(db.data_used_mb, 2)}
                          </td>
                          <td className="py-2.5 px-3">
                            <div className="flex items-center justify-between text-[11px] font-mono mb-1">
                              <span className={`font-bold ${getPctColorClass(dp)}`}>{dp.toFixed(1)}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${getPctBgClass(dp)}`}
                                style={{ width: `${Math.min(dp, 100)}%` }}
                              />
                            </div>
                          </td>
                          <td className="py-2.5 px-3 font-mono text-gray-700 dark:text-gray-300">
                            {formatNumber(db.log_allocated_mb, 2)}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-gray-700 dark:text-gray-300">
                            {formatNumber(db.log_used_mb, 2)}
                          </td>
                          <td className="py-2.5 px-3">
                            <div className="flex items-center justify-between text-[11px] font-mono mb-1">
                              <span className="font-bold text-gray-700 dark:text-gray-300">{lp.toFixed(1)}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500"
                                style={{ width: `${Math.min(lp, 100)}%` }}
                              />
                            </div>
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                                db.status === 'warning'
                                  ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                                  : db.status === 'critical'
                                  ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-500/30'
                                  : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
                              }`}
                            >
                              {db.status || 'OK'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SUB-TAB 2: DATABASE FILES ── */}
        {activeTab === 'files' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter file name, physical path, type..."
                  value={fileSearch}
                  onChange={(e) => setFileSearch(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="text-xs text-gray-500">
                Total Files: <span className="font-semibold">{filteredFiles.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">Logical Name</th>
                    <th className="py-2.5 px-3">Type</th>
                    <th className="py-2.5 px-3">Size (MB)</th>
                    <th className="py-2.5 px-3">Used (MB)</th>
                    <th className="py-2.5 px-3">Growth</th>
                    <th className="py-2.5 px-3">Max Size</th>
                    <th className="py-2.5 px-3">Physical Path</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-mono text-[11px]">
                  {filteredFiles.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-200 font-sans">
                        No database files found
                      </td>
                    </tr>
                  ) : (
                    filteredFiles.map(({ dbName, file }) => {
                      const growthStr =
                        file.growth_pct != null
                          ? `${file.growth_pct}%`
                          : file.growth_mb != null
                          ? `${file.growth_mb} MB`
                          : '—';
                      return (
                        <tr
                          key={`${dbName}-${file.file_id}-${file.name}`}
                          className="hover:bg-gray-50/70 dark:hover:bg-gray-700/40 transition-colors"
                        >
                          <td className="py-2.5 px-3 font-semibold font-sans text-gray-900 dark:text-white">
                            {dbName}
                          </td>
                          <td className="py-2.5 px-3 font-semibold text-gray-800 dark:text-gray-200">
                            {file.name}
                          </td>
                          <td className="py-2.5 px-3">
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                                file.type === 'ROWS'
                                  ? 'bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30'
                                  : 'bg-purple-50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800/30'
                              }`}
                            >
                              {file.type}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300">
                            {formatNumber(file.size_mb, 2)}
                          </td>
                          <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300">
                            {file.used_mb != null ? formatNumber(file.used_mb, 2) : '—'}
                          </td>
                          <td className="py-2.5 px-3 text-gray-600 dark:text-gray-200">{growthStr}</td>
                          <td className="py-2.5 px-3 text-gray-600 dark:text-gray-200">
                            {file.max_size || 'unlimited'}
                          </td>
                          <td className="py-2.5 px-3 text-gray-500 dark:text-gray-200 truncate max-w-xs group">
                            <div className="flex items-center gap-1.5">
                              <span title={file.physical_name} className="truncate">
                                {file.physical_name}
                              </span>
                              <button
                                onClick={() => handleCopy(file.physical_name)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-500 cursor-pointer"
                                title="Copy file path"
                              >
                                {copiedPath === file.physical_name ? (
                                  <Check className="w-3 h-3 text-emerald-500" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SUB-TAB: AUTOMATED FINDINGS ── */}
        {activeTab === 'findings' && (
          <div className="p-4 space-y-4">
            {/* Advisory Note */}
            {findings?.note && (
              <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="font-semibold text-amber-900 dark:text-amber-100">Advisory Notice:</span>
                  <p className="leading-relaxed text-[11px] opacity-90">{findings.note}</p>
                </div>
              </div>
            )}

            {/* Severity Summary Bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1 pb-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium mr-1">
                  Total reported: <span className="font-bold text-gray-800 dark:text-gray-200">{findings?.count ?? filteredFindings.length}</span> findings
                </span>
                {findings?.by_severity &&
                  Object.entries(findings.by_severity).map(([sev, count]) => {
                    const color =
                      sev === 'critical'
                        ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
                        : sev === 'warning'
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                          : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
                    return (
                      <span
                        key={sev}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${color}`}
                      >
                        {count} {sev}
                      </span>
                    );
                  })}
              </div>

              <span className="text-[11px] text-gray-400 dark:text-gray-500 font-mono">
                Showing {filteredFindings.length} of {findings?.count ?? filteredFindings.length}
              </span>
            </div>

            {/* Findings List */}
            {filteredFindings.length === 0 ? (
              <div className="py-12 text-center text-xs text-gray-500 dark:text-gray-400">
                No automated findings matching the current filters.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFindings.map((item, idx) => {
                  const sevColor =
                    item.severity === 'critical'
                      ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 border-rose-200 dark:border-rose-500/30'
                      : item.severity === 'warning'
                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                        : 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';

                  return (
                    <div
                      key={idx}
                      className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-all space-y-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${sevColor}`}
                          >
                            {item.severity}
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {item.category?.replace(/_/g, ' ')}
                          </span>
                          {item.database && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-medium bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20">
                              {item.database}
                            </span>
                          )}
                        </div>
                        {item.object && (
                          <span className="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300 truncate max-w-md">
                            {item.object}
                          </span>
                        )}
                      </div>

                      <div className="space-y-1.5 text-xs">
                        <div className="text-gray-900 dark:text-white font-medium leading-relaxed">
                          {item.finding}
                        </div>
                        {item.impact && (
                          <div className="text-gray-600 dark:text-gray-300 text-[11px] flex items-start gap-1.5">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 min-w-[55px]">
                              Impact:
                            </span>
                            <span>{item.impact}</span>
                          </div>
                        )}
                        {item.remediation && (
                          <div className="text-gray-600 dark:text-gray-300 text-[11px] flex items-start gap-1.5">
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400 min-w-[55px]">
                              Remedy:
                            </span>
                            <span>{item.remediation}</span>
                          </div>
                        )}
                      </div>

                      {item.command && (
                        <div className="pt-1">
                          <div className="flex items-center justify-between bg-gray-900 text-gray-100 dark:bg-black/60 rounded-lg p-2.5 font-mono text-[11px] border border-gray-700/60 overflow-x-auto">
                            <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                              <Terminal className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                              <span className="truncate select-all">{item.command}</span>
                            </div>
                            <button
                              onClick={() => handleCopy(item.command || '')}
                              className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-[10px] font-medium flex-shrink-0 cursor-pointer"
                              title="Copy command to clipboard"
                            >
                              {copiedPath === item.command ? (
                                <>
                                  <Check className="w-3 h-3 text-emerald-400" />
                                  <span className="text-emerald-400">Copied</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3" />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── SUB-TAB 3: RECENT EVENTS ── */}
        {activeTab === 'events' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Audit events reported in current telemetry window
              </span>
              <span className="text-xs font-mono text-gray-200">UTC</span>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              {filteredEvents.length === 0 ? (
                <div className="py-8 text-center text-xs text-gray-200">No recent events recorded</div>
              ) : (
                filteredEvents.map((evt, idx) => (
                  <div
                    key={idx}
                    className="p-3 hover:bg-gray-50/70 dark:hover:bg-gray-700/40 transition-colors flex items-start justify-between gap-3 text-xs"
                  >
                    <div className="flex items-start gap-2.5">
                      <span className="font-mono font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-[11px] shrink-0">
                        {evt.database || 'master'}
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800 dark:text-gray-200">
                            {evt.event || 'Audit Event'}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                              evt.severity === 'warning'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                            }`}
                          >
                            {evt.severity || 'WARN'}
                          </span>
                        </div>
                        <p className="text-gray-600 dark:text-gray-200 text-[11px] mt-0.5">
                          {evt.details}
                        </p>
                      </div>
                    </div>
                    {evt.time && (
                      <span className="text-[10px] font-mono text-gray-200 shrink-0">
                        {formatDate(evt.time)}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Config alert note */}
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <div>
                <span className="font-semibold">Note:</span> If AUTO_CLOSE is set to ON on user databases, it closes
                connections and flushes plan caches when idle, introducing query latency on reconnect.
              </div>
            </div>
          </div>
        )}

        {/* ── SUB-TAB 4: STALE STATISTICS ── */}
        {activeTab === 'stale_stats' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter table, schema, statistic or reason..."
                  value={statSearch}
                  onChange={(e) => setStatSearch(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="text-xs text-gray-500">
                Stale items: <span className="font-semibold">{filteredStaleStats.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">Table</th>
                    <th className="py-2.5 px-3">Statistic</th>
                    <th className="py-2.5 px-3">Rows</th>
                    <th className="py-2.5 px-3">Modifications</th>
                    <th className="py-2.5 px-3">Threshold</th>
                    <th className="py-2.5 px-3">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-mono text-[11px]">
                  {filteredStaleStats.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-200 font-sans">
                        No stale statistics reported for this filter
                      </td>
                    </tr>
                  ) : (
                    filteredStaleStats.map((st, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50/70 dark:hover:bg-gray-700/40 transition-colors"
                      >
                        <td className="py-2.5 px-3 font-semibold font-sans text-gray-900 dark:text-white">
                          {st.database || '—'}
                        </td>
                        <td className="py-2.5 px-3 text-gray-800 dark:text-gray-200">
                          {st.schema ? `${st.schema}.${st.table}` : st.table}
                        </td>
                        <td className="py-2.5 px-3 text-purple-700 dark:text-purple-300 font-bold truncate max-w-xs">
                          {st.stat}
                        </td>
                        <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300">
                          {formatNumber(st.rows, 0)}
                        </td>
                        <td className="py-2.5 px-3 text-amber-600 dark:text-amber-400 font-bold">
                          {formatNumber(st.modifications, 0)}
                        </td>
                        <td className="py-2.5 px-3 text-gray-600 dark:text-gray-200">
                          {formatNumber(st.threshold, 0)}
                        </td>
                        <td className="py-2.5 px-3 font-sans">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">
                            {st.reason || 'stale'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SUB-TAB 5: DBCC CHECKDB INTEGRITY ── */}
        {activeTab === 'checkdb' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Database consistency & integrity verification status</span>
              <span>Checked overdue threshold: 7 days</span>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">Last Good CHECKDB</th>
                    <th className="py-2.5 px-3">Diagnostic Details</th>
                    <th className="py-2.5 px-3 text-right">Integrity Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredCheckDB.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-gray-200">
                        No integrity check records available
                      </td>
                    </tr>
                  ) : (
                    filteredCheckDB.map((c, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50/70 dark:hover:bg-gray-700/40 transition-colors"
                      >
                        <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                          <Database className="w-3 h-3 text-gray-200 shrink-0" />
                          <span>{c.database}</span>
                        </td>
                        <td className="py-2.5 px-3 font-mono text-[11px] text-gray-600 dark:text-gray-200">
                          {formatDate(c.last_good_checkdb)}
                        </td>
                        <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300 text-xs">
                          {c.detail || 'DBCC CHECKDB status reported'}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                              !c.last_good_checkdb
                                ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                                : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
                            }`}
                          >
                            {!c.last_good_checkdb ? 'OVERDUE' : 'HEALTHY'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MSSQLStorageView;
export { MSSQLStorageView as MSSQLStorage };
